// Utility to auto-generate action types from endpoint key
function generateActionTypes(endpointKey) {
  const base = endpointKey;
  return {
    successActionType: base + '_SUCCESS',
    failureActionType: base + '_FAILURE',
  };
}
import axios from 'axios';

// Generic async API request utility for use outside React (e.g., thunks)
export async function apiRequest(endpointKey, params = {}, methodOverride, headersOverride) {
  const config = buildApiConfig(endpointKey, params, methodOverride, headersOverride);
  const axiosConfig = {
    url: config.endpoint.url,
    method: config.endpoint.method,
    headers: config.headers,
  };
  if (config.endpoint.method !== 'GET' && config.params && Object.keys(config.params).length) {
    axiosConfig.data = config.params;
  }
  const response = await axios(axiosConfig);
  return response.data;
}
/*
EXPLANATION OF API LAYER:

1. buildApiConfig:
   - Centralizes all endpoint config (URL, method, headers, params) in one place.
   - Returns a config object for a given endpointKey and params.

2. apiRequest:
   - Plain async function for use outside React (e.g., Redux thunks, utility scripts).
   - Uses buildApiConfig to get config, then makes the HTTP request (now with axios).
   - Returns the parsed response data.

3. useApiRequest:
   - React hook for use inside React components.
   - Uses buildApiConfig to get config, then calls useAPI (a custom hook) to manage loading, error, and data states in React.
   - Returns { data, error, loading, refetch } for easy use in UI.

4. useAPI:
   - Your custom React hook (in services/api/useAPI.js) that actually performs the API call and manages state for React.
   - Used internally by useApiRequest.

This structure ensures:
  - All API config is DRY and centralized (buildApiConfig).
  - All HTTP logic is DRY and centralized (apiRequest for async, useApiRequest/useAPI for React).
  - No duplicate fetch/axios logic anywhere in your codebase.

// Example: Using apiRequest in a Redux thunk to fetch data and store in Redux
//
// import { apiRequest } from './apiClient';
// import { setParcelHistory } from '../redux/slices/bookingSlice';
//
// export const fetchParcelHistory = () => async (dispatch) => {
//   try {
//     const data = await apiRequest('getParcelsList');
//     dispatch(setParcelHistory(data));
//   } catch (error) {
//     // handle error, e.g. dispatch(setError(error.message))
//   }
// };
*/
import { endpoints } from './endpoints';
import { getJWT } from './authHelpers';

// Generic config builder for any endpoint
export function buildApiConfig(endpointKey, params = {}, methodOverride, headersOverride) {
  const endpoint = endpoints[endpointKey];
  if (!endpoint) throw new Error(`Endpoint ${endpointKey} not found`);
  const jwt = getJWT();
  // Auto-generate action types if not provided
  const { successActionType, failureActionType } =
    endpoint.successActionType && endpoint.failureActionType
      ? {
          successActionType: endpoint.successActionType,
          failureActionType: endpoint.failureActionType,
        }
      : generateActionTypes(endpointKey);

  // Handle URL as function or string
  let url =
    typeof endpoint.url === 'function' ? endpoint.url(...Object.values(params)) : endpoint.url;

  const method = methodOverride || endpoint.method;

  // For GET requests with function URLs, params are already in the URL
  // For GET requests with string URLs, append params as query string
  if (
    method === 'get' &&
    typeof endpoint.url !== 'function' &&
    params &&
    Object.keys(params).length
  ) {
    const query = new URLSearchParams(params).toString();
    url += (url.includes('?') ? '&' : '?') + query;
  }

  // Ensure method is lowercase string for axios
  const normalizedMethod =
    typeof method === 'string' ? method.toLowerCase() : endpoint.method?.toLowerCase() || 'get';

  return {
    endpoint: {
      url,
      method: normalizedMethod,
    },
    params,
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      ...headersOverride,
    },
    successActionType,
    failureActionType,
  };

  return {
    endpoint: {
      url,
      method,
    },
    params,
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      ...headersOverride,
    },
    successActionType,
    failureActionType,
  };
}

// React hook for any API call
export function callApiRequest(endpointKey, params = {}, headersOverride) {
  const config = buildApiConfig(endpointKey, params, undefined, headersOverride);
  return config;
  //   return CallAPI(config.endpoint, config.params, config.headers);
}
